import TileWrapper from '../TileWrapper'
import { useMemo } from 'react'
import { useTile } from '../../context'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  useOverviewData,
  usePageInfo,
  useTeamAuditData,
} from 'app/frontend/base/config/queries'
import { AuditContext } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamAudit/context'
import AuditTable from 'app/frontend/base/pages/TeamDashboard/subpages/TeamAudit/components/AuditTable'
import AuditCategory, {
  auditCategories,
} from 'app/frontend/types/custom/AuditCategory'
import { Skeleton } from '@/components/ui/skeleton'

const CATEGORY_LABELS: Record<AuditCategory, string> = {
  summary: 'Summary',
  gear_overview: 'Gear (overview)',
  gear_upgrades: 'Gear (upgrades)',
  enchantments: 'Enchantments',
  vault: 'Vault',
  raids_weekly: 'Raids (this week)',
  raids_overall: 'Raids (overall)',
  dungeons_weekly: 'Dungeons (weekly)',
  dungeons_overall: 'Dungeons (season)',
  collections: 'Collections',
  reputations: 'Reputations',
  pvp: 'PvP',
}

export const CATEGORIES = auditCategories.map((category) => ({
  value: category,
  label: CATEGORY_LABELS[category],
}))

const CONFIG_TO_FILTER_KEY: Record<string, string> = {
  ilvl_display: 'totalIlvlDisplay',
  slot_display: 'slotDisplay',
  minimum_quality: 'minimumQuality',
  enchant_view: 'enchantView',
  raid_difficulty: 'raidDifficulty',
  minimum_level: 'minimumLevel',
}

const OverviewTableTile = () => {
  const { tile } = useTile()
  const { team } = useTeam()
  const {
    data: { currentSeason },
  } = usePageInfo()

  const category = (tile.config?.category as AuditCategory) || 'summary'
  const weeksAgo = Number(tile.config?.weeks_ago) || 0

  const filterOverrides = useMemo(() => {
    const overrides: Record<string, string | undefined> = {}
    for (const [configKey, filterKey] of Object.entries(CONFIG_TO_FILTER_KEY)) {
      const value = tile.config?.[configKey] as string | undefined
      if (value !== undefined && value !== '') {
        overrides[filterKey] = value
      }
    }
    return overrides
  }, [tile.config])

  const { isSuccess: overviewLoaded } = useOverviewData(team.id)
  const { data, isPending } = useTeamAuditData(team.id, overviewLoaded)

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter(
        (member) => member.teamRank?.spreadsheet_overview_visibility,
      ) ?? []
    ).map((member) => ({
      ...member,
      data: data?.[member.characterReference.id]?.last_refresh,
      gear: data?.[member.characterReference.id]?.current_gear,
      keystones: data?.[member.characterReference.id]?.keystones,
      mythicRatingColor:
        data?.[member.characterReference.id]?.mythic_rating_color,
    }))
  }, [data, team.members])

  if (isPending) {
    return (
      <TileWrapper className='col-span-full'>
        <Skeleton className='h-80 w-full' />
      </TileWrapper>
    )
  }

  return (
    <TileWrapper className='col-span-full' inContainer={false}>
      <AuditContext.Provider
        value={{
          selectedSeason: currentSeason,
          setSelectedSeason: () => null,
          visibleMembers,
          weeksAgo,
          setWeeksAgo: () => null,
          showingRanks: false,
          setShowingRanks: () => null,
          activeView: 'list',
          setActiveView: () => null,
          filterOverrides,
        }}
      >
        <AuditTable key={category} category={category} embedded />
      </AuditContext.Provider>
    </TileWrapper>
  )
}

export default OverviewTableTile
